import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSnackbar } from "notistack";

import { useAuth } from "../../hooks/useAuth";
import {
  GET_SUPPORT_CONTACTS,
  CREATE_SUPPORT_CONTACT,
  UPDATE_SUPPORT_CONTACT,
  DELETE_SUPPORT_CONTACT,
} from "./queries";
import SupportContactDialog from "./SupportContactDialog";
import { SupportContactRow, SupportContactDraft } from "./types";

/**
 * Support Contact — the customer-facing numbers, editable here instead of being
 * hard-coded in the portal frontend.
 *
 * The list is stored in oem_tool_db (support_contact) and reaches the customer
 * through recharge-service, which caches it for five minutes. So a change here
 * shows up on the recharge portal within about five minutes, with no build and
 * no redeploy anywhere.
 *
 * The portal shows every number that is currently available, in priority order.
 * Out of hours it still shows the top one with its opening times, so a customer
 * with a stuck payment is never left without someone to call — which is why
 * "Active" and "open right now" are two different things here, and why deleting
 * the last number is refused by the backend in favour of turning it off.
 */

const CONTACT_KEY = "recharge_support";

const formatUpdatedAt = (epochSeconds: number | null) =>
  epochSeconds ? new Date(epochSeconds * 1000).toLocaleString() : "never";

const StatusChip: React.FC<{ row: SupportContactRow }> = ({ row }) => {
  if (!row.isActive) {
    return <Chip size="small" label="Turned off" sx={{ bgcolor: "#F3F4F6", color: "#6B7280" }} />;
  }
  return row.isAvailable ? (
    <Chip size="small" label="Available now" sx={{ bgcolor: "#DCFCE7", color: "#166534" }} />
  ) : (
    <Chip size="small" label="Closed now" sx={{ bgcolor: "#FEF3C7", color: "#B45309" }} />
  );
};

const SupportContact: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { canCreate, canUpdate, canDelete } = useAuth();
  const mayCreate = canCreate("SUPPORT-CONTACT");
  const mayUpdate = canUpdate("SUPPORT-CONTACT");
  const mayDelete = canDelete("SUPPORT-CONTACT");

  const { data, loading, error, refetch } = useQuery(GET_SUPPORT_CONTACTS, {
    variables: { contactKey: CONTACT_KEY },
  });

  const [createContact, { loading: creating }] = useMutation(CREATE_SUPPORT_CONTACT);
  const [updateContact, { loading: updating }] = useMutation(UPDATE_SUPPORT_CONTACT);
  const [deleteContact] = useMutation(DELETE_SUPPORT_CONTACT);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SupportContactRow | null>(null);

  const rows: SupportContactRow[] = data?.supportContacts ?? [];

  // Every mutation reports the same way — the backend hands back a reason on
  // failure (a rejected number, the last-number delete guard), and it is more
  // useful to the operator than anything this component could invent.
  const runMutation = async (
    run: () => Promise<any>,
    pick: (data: any) => { success: boolean; message?: string | null } | undefined,
  ) => {
    try {
      const res = await run();
      const result = pick(res.data);
      if (result?.success) {
        enqueueSnackbar(
          `${result.message || "Saved"} — customers see the change within about 5 minutes.`,
          { variant: "success" },
        );
        await refetch();
        return true;
      }
      enqueueSnackbar(result?.message || "Failed", { variant: "error" });
      return false;
    } catch (err: any) {
      enqueueSnackbar(err?.message || "Failed", { variant: "error" });
      return false;
    }
  };

  const handleSave = async (draft: SupportContactDraft) => {
    const ok = editing
      ? await runMutation(
          () => updateContact({ variables: { contactId: editing.contactId, input: draft } }),
          (d) => d?.updateSupportContact,
        )
      : await runMutation(
          () => createContact({ variables: { input: { ...draft, contactKey: CONTACT_KEY } } }),
          (d) => d?.createSupportContact,
        );
    if (ok) {
      setDialogOpen(false);
      setEditing(null);
    }
  };

  const handleDelete = async (row: SupportContactRow) => {
    if (!window.confirm(`Remove ${row.phone} from the recharge portal?`)) return;
    await runMutation(
      () => deleteContact({ variables: { contactId: row.contactId } }),
      (d) => d?.deleteSupportContact,
    );
  };

  if (loading && !rows.length) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !rows.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Could not load support contacts: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            Support Contact
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Numbers shown to customers on the recharge portal, best first. Saving here
            changes what they see — no app release required.
          </Typography>
        </Box>
        {mayCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Add number
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          p: 1.5,
          mb: 2,
          bgcolor: "#EFF6FF",
          border: "1px solid #DBEAFE",
          borderRadius: 1.5,
        }}
      >
        <InfoOutlinedIcon sx={{ color: "#1E40AF", fontSize: 18, mt: "1px" }} />
        <Typography variant="caption" color="#1E40AF">
          The portal lists every number that is open at the time the customer loads the
          page, lowest priority number first. Outside all opening hours it still shows the
          top number along with its hours, so nobody is left without a line to call.
          Turning a number <b>off</b> is different — it disappears from the portal entirely.
          {!mayUpdate && " You have read-only access; changing a number needs SUPPORT-CONTACT update permission."}
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #E5E7EB", borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#F9FAFB" }}>
              <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Number</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Hours (IST)</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Last changed</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.contactId} hover>
                <TableCell>{row.priority}</TableCell>
                <TableCell>{row.label || "—"}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{row.phone}</TableCell>
                <TableCell>{row.hoursLabel}</TableCell>
                <TableCell><StatusChip row={row} /></TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {formatUpdatedAt(row.updatedAt)}
                    {row.updatedBy ? ` · user ${row.updatedBy}` : ""}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={mayUpdate ? "Edit" : "Update permission required"}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!mayUpdate}
                        onClick={() => {
                          setEditing(row);
                          setDialogOpen(true);
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !mayDelete
                        ? "Delete permission required"
                        : rows.length <= 1
                        ? "The only number — turn it off instead"
                        : "Remove"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!mayDelete || rows.length <= 1}
                        onClick={() => handleDelete(row)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No support numbers configured — the portal is falling back to its
                    built-in default. Add one to take control of it.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <SupportContactDialog
        open={dialogOpen}
        contact={editing}
        saving={creating || updating}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />
    </Box>
  );
};

export default SupportContact;
